// KardexProductoXL - Sprint 44 Fase 3 Vista 5.
// Kardex (NIIF) por producto XL. Header con back + producto info + stats card.
// Tabla de movimientos con badges, columnas destacadas (existencias / CP).
// Solo lectura sobre /productos/:id/kardex. Cero backend touch.
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useKardexXL from './hooks/useKardexXL';
import XLTable from '../../components/xl/XLTable';
import XLBadge from '../../components/xl/XLBadge';
import XLSkeleton from '../../components/xl/XLSkeleton';
import { fmtMoney } from '../../utils/money';
import { fmtNum, fmtFecha, TIPO_VARIANT, TIPO_LABEL } from './utils';

const CSS_ID = 'xl-kardex-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-kardex-hd {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-kardex-hd__left { display: flex; flex-direction: column; gap: 6px }
.xl-kardex-hd__back {
  font: 500 13px/1 var(--xl-font-sans);
  color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
  align-self: flex-start;
}
.xl-kardex-hd__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-kardex-hd__back:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-kardex-hd__title { margin: 0; font-size: 22px; font-weight: 600; line-height: 1.2 }
.xl-kardex-hd__subtitle { margin: 0; font-size: 13px; color: var(--xl-color-text-secondary) }

.xl-kardex-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  padding: 16px;
  box-shadow: var(--xl-shadow-sm);
  min-width: 360px;
}
.xl-kardex-stat__label {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--xl-color-text-muted); font-weight: 700;
}
.xl-kardex-stat__value {
  font-family: var(--xl-font-mono);
  font-size: 14px; font-weight: 600;
  color: var(--xl-color-text);
  margin-top: 4px;
}
.xl-kardex-stat__value--success {
  color: var(--xl-color-success);
}

.xl-banner {
  padding: 10px 14px; margin-bottom: 12px;
  border-radius: var(--xl-radius-md); font-size: 13px;
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
}
.xl-loading { display: flex; flex-direction: column; gap: 10px }
.xl-empty {
  padding: 32px 16px;
  text-align: center;
  color: var(--xl-color-text-muted);
  font-size: 13px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
}
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
.xl-mono--strong { font-family: var(--xl-font-mono); font-weight: 600 }
`;

export default function KardexProductoXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const { id } = useParams();
  const navigate = useNavigate();
  const productoId = id ? Number(id) : null;
  const kx = useKardexXL(productoId);

  const columns = useMemo(() => ([
    { key: 'fecha', label: 'Fecha',
      render: (m) => <span className="xl-mono">{fmtFecha(m.fecha_movimiento)}</span> },
    { key: 'tipo', label: 'Tipo',
      render: (m) => (
        <XLBadge variant={TIPO_VARIANT[m.tipo_movimiento] || 'neutral'} dot>
          {TIPO_LABEL[m.tipo_movimiento] || m.tipo_movimiento}
        </XLBadge>
      ) },
    { key: 'origen', label: 'Origen',
      render: (m) => (
        <span className="xl-mono">
          {m.origen}{m.origen_id ? ('#' + m.origen_id) : ''}
        </span>
      ) },
    { key: 'cantidad', label: 'Cant.', align: 'right',
      render: (m) => <span className="xl-mono">{fmtNum(m.cantidad)}</span> },
    { key: 'costo_unitario', label: 'Costo unit.', align: 'right',
      render: (m) => <span className="xl-mono">{fmtNum(m.costo_unitario)}</span> },
    { key: 'costo_total', label: 'Costo total', align: 'right',
      render: (m) => <span className="xl-mono">{fmtNum(m.costo_total, 0)}</span> },
    { key: 'existencias_antes', label: 'Exist. antes', align: 'right',
      render: (m) => <span className="xl-mono">{fmtNum(m.existencias_antes)}</span> },
    { key: 'existencias_despues', label: 'Exist. despues', align: 'right',
      render: (m) => <span className="xl-mono--strong">{fmtNum(m.existencias_despues)}</span> },
    { key: 'cp_antes', label: 'CP antes', align: 'right',
      render: (m) => <span className="xl-mono">{fmtNum(m.costo_promedio_antes)}</span> },
    { key: 'cp_despues', label: 'CP despues', align: 'right',
      render: (m) => <span className="xl-mono--strong">{fmtNum(m.costo_promedio_despues)}</span> },
    { key: 'descripcion', label: 'Descripcion',
      render: (m) => m.descripcion || '\u2014' },
  ]), []);

  if (kx.loading) {
    return (
      <div className="xl-page">
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="line" width="30%" height={20} />
          <XLSkeleton variant="rect" height={120} />
          <XLSkeleton variant="rect" height={240} />
        </div>
      </div>
    );
  }

  if (kx.err) {
    return (
      <div className="xl-page">
        <div className="xl-banner" role="alert">{kx.err}</div>
      </div>
    );
  }

  if (!kx.producto) {
    return (
      <div className="xl-page">
        <div className="xl-empty" role="status">Producto no encontrado.</div>
      </div>
    );
  }

  const p = kx.producto;
  const moneda = 'COP';

  return (
    <div className="xl-page">
      <header className="xl-kardex-hd">
        <div className="xl-kardex-hd__left">
          <button type="button" className="xl-kardex-hd__back"
            onClick={() => navigate('/inventario')}>
            {'\u2190 Inventario'}
          </button>
          <h2 className="xl-kardex-hd__title">Kardex \u00b7 {p.codigo}</h2>
          <p className="xl-kardex-hd__subtitle">{p.nombre}</p>
        </div>
        <div className="xl-kardex-stats" role="group" aria-label="Estado actual del producto">
          <div>
            <div className="xl-kardex-stat__label">Existencias</div>
            <div className="xl-kardex-stat__value">
              {fmtNum(p.stock)} {p.unidad_medida || ''}
            </div>
          </div>
          <div>
            <div className="xl-kardex-stat__label">Costo promedio</div>
            <div className="xl-kardex-stat__value">
              {fmtMoney(p.costo_promedio, moneda)}
            </div>
          </div>
          <div>
            <div className="xl-kardex-stat__label">Valorizacion</div>
            <div className="xl-kardex-stat__value xl-kardex-stat__value--success">
              {fmtMoney(kx.valorizacion, moneda)}
            </div>
          </div>
        </div>
      </header>

      {kx.movs.length === 0 ? (
        <div className="xl-empty" role="status">
          Este producto aun no tiene movimientos de kardex.
        </div>
      ) : (
        <XLTable
          ariaLabel="Movimientos de kardex"
          columns={columns}
          data={kx.movs}
          loading={false}
          rowKey="id"
          emptyMessage="Sin movimientos."
        />
      )}
    </div>
  );
}
